import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { currencyFormat } from 'helpers/utils';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Transaction } from 'hooks/transactions/useTransactionsData';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import Badge from 'components/base/Badge';
import { Link } from 'react-router-dom';

// Create a type that includes all properties from ColumnDef<Transaction> plus visible property
export type TransactionColumnDef = ColumnDef<Transaction, any> & {
  visible?: boolean;
  accessorKey?: string;
  id?: string;
}

// Helper function to get the style class based on transaction type
const getTransactionTypeClass = (type: string): string => {
  return type === 'Manual' ? 'text-success' : 'text-danger';
};

export const transactionsTableColumns: TransactionColumnDef[] = [
  {
    accessorKey: 'transaction_id',
    header: 'Transaction ID',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      
      // Extract first two characters for the badge
      const firstTwoChars = original.transaction_id.substring(0, 2);
      const remainingChars = original.transaction_id.substring(2);
      
      // Only make transaction ID clickable when type is "Manual"
      const content = (
        <>
          <Badge 
            variant="phoenix" 
            bg={original.type === 'Manual' ? 'success' : 'danger'} 
            className="me-1"
          >
            {firstTwoChars}
          </Badge>
          {remainingChars}
        </>
      );
      
      // Conditionally render Link or span based on transaction type
      return original.type === 'Manual' ? (
        <Link 
          to={`/transactions/${original.entity_transaction_id}`}
          className={`fw-semibold text-decoration-none`}
        >
          {content}
        </Link>
      ) : (
        <span className="fw-semibold">
          {content}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '14%' }, className: 'pe-3 ps-0' },
      cellProps: { className: 'ps-0' }
    }
  },
  {
    accessorKey: 'bank_transaction_id',
    header: 'Bank Transaction ID',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      return (
        <span className={`fw-semibold text-capitalize `}>
          {original.bank_transaction_id}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '14%' } },
      cellProps: { className: 'text-body' }
    }
  },
  {
    accessorKey: 'message',
    header: 'Message',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      return (
        <span className={`text-capitalize fw-semibold `}>
          {original.message}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '16%' } },
      cellProps: { className: 'text-body' }
    }
  },
  {
    accessorKey: 'formatted_date',
    header: 'Transaction Date',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      return (
        <span className={`fs-9 text-capitalize fw-semibold `}>
          {original.formatted_date}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '12%' } },
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'transaction_amount',
    header: 'Transaction Amount',
    visible: true,
    cell: ({ row: { original } }) => {
      const isManual = original.type === 'Manual';
      const bgClass = getTransactionTypeClass(original.type);
      const prefix = isManual ? '+' : '–';
      return (
        <span className={`currency-value ${bgClass} fw-bold `}>
          {prefix}{Math.abs(original.transaction_amount).toFixed(2)} DT
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' } },
      cellProps: { className: 'text-end fw-semibold' }
    }
  },
  {
    accessorKey: 'total_amount',
    header: 'Total Amount',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      return (
        <span className={`currency-value   fw-semibold `}>
          {currencyFormat(original.total_amount)}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }},
      cellProps: { className: 'text-end fw-semibold' }
    }
  },
  {
    accessorKey: 'comission_amount',
    header: 'Commission Amount',
    visible: true,
    cell: ({ row: { original } }) => {
      const typeClass = getTransactionTypeClass(original.type);
      return (
        <span className={`currency-value  fw-semibold `}>
          {currencyFormat(original.comission_amount)}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }, className: '' },
      cellProps: { className: ' fw-semibold' }
    }
  }
];

interface TransactionsTableProps {
  visibleColumns?: Record<string, boolean>;
  onToggleColumn?: (columnId: string, visible: boolean) => void;
  totalCount?: number;
}

const TransactionsTable: FC<TransactionsTableProps> = ({ totalCount }) => {
  const { t } = useTranslation();
  const table = useAdvanceTableContext();
  
  // Create columns with translated headers and directly update them
  useMemo(() => {
    // Apply translations to the table headers by directly modifying the column definitions
    table.getAllColumns().forEach(column => {
      const accessorKey = column.id;
      if (accessorKey) {
        const translatedHeader = t(`transactions.columns.${accessorKey}`, column.columnDef.header as string);
        if (translatedHeader) {
          column.columnDef.header = translatedHeader;
        }
      }
    });
  }, [t, table]);
  return (
    <div>
      <AdvanceTable 
        tableProps={{ 
          className: 'phoenix-table fs-9',
          striped: true,
          hover: true
        }}
      />
      <AdvanceTableFooter 
        pagination 
        totalCount={totalCount} 
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default TransactionsTable;
